import { getGuideDuration, getGuideId } from '@/utils/media';
import {
    MediaProgramMetadata,
    MediaProgramWithGuides,
    MediaGuideMetadata,
} from '@/utils/media/types';
import {
    EnumProductAnalyticsEvent,
    EventProperties,
} from '@calm/evexia-analytics';
import { useTracking } from '@rhg/libs/analytics';
import { getScreenName } from '@rhg/libs/analytics/track-page-nav';
import useAuth from '@rhg/libs/auth0/use-auth';
import { logger } from '@rhg/libs/logger';
import { useMediaPreferences } from '@rhg/providers/media-preferences';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useProvidedProgramContext } from '../use-provided-program-context';

interface TrackSessionEventProps {
    eventName: EnumProductAnalyticsEvent;
    trackProps?: EventProperties;
    guide: MediaGuideMetadata;
    program?: MediaProgramWithGuides;
    packClass?: string;
    percentCompleted?: number;
}

type TrackPackItemEventPackGuide =
    | {
          guide: MediaGuideMetadata;
          packTitle?: string;
      }
    | { guide?: never; packTitle?: string };

interface TrackPackItemEventBase {
    eventName: EnumProductAnalyticsEvent;
    trackProps?: EventProperties;
    program?: MediaProgramWithGuides | MediaProgramMetadata;
    /**
     * When true, ignore the ambient analytics props (e.g. home pack-row
     * context: PackClass, RowIndex, PackDisplayType, RowItemIndex,
     * PackItemName) inherited from any enclosing
     * `AnalyticsEventPropsProvider`. Used by card CTA buttons (e.g.
     * program-card banner actions) where analytics convention is to
     * exclude those properties even when fired from inside a home row.
     */
    clearAmbient?: boolean;
}

type TrackPackItemEventProps = TrackPackItemEventBase &
    TrackPackItemEventPackGuide;

export const useTrackSessionEvent = () => {
    const { program: _program, programMetadata } = useProvidedProgramContext();
    const programFromContext = _program ?? programMetadata;
    const { pathname, query } = useRouter();
    const { track } = useTracking();
    const { isAuthenticated } = useAuth();

    const { isShowingSubtitles } = useMediaPreferences();

    const screenName = useMemo(
        () =>
            getScreenName(
                { pathname, query },
                { IsAuthenticated: isAuthenticated }
            ),
        [pathname, query, isAuthenticated]
    );

    const trackSessionEvent = useCallback(
        ({
            eventName,
            guide,
            program: providedProgram,
            packClass,
            percentCompleted,
            trackProps,
        }: TrackSessionEventProps) => {
            const trackFilterName = query.category;
            const program = providedProgram ?? programFromContext;
            if (!guide) {
                logger.error(
                    'No guide provided',
                    { eventName, guide },
                    {
                        name: 'No guide provided',
                        message:
                            'useTrackSessionEvent: trackSessionEvent : No guide provided',
                    }
                );
                return;
            }

            // P2 analytics will come in a future ticket
            // Caller-provided trackProps are spread LAST so live player state
            // (e.g. PercentCompleted, IsUsingSubtitles from the player bundle)
            // wins over the generic base values built here.
            track(eventName, {
                GuideId: getGuideId(guide),
                GuideLanguage: program?.language,
                GuideLength: getGuideDuration(guide),
                GuideName: guide.title,
                IsClinicalProgram: program?.isClinical,
                IsTrailer: guide.isTrailer,
                IsUsingSubtitles: isShowingSubtitles,
                MediaType: guide.assets?.[0]?.type,
                NarratorName: program?.narrator?.name,
                // SourceScreenIndex: 'REPLACE THIS',  // P2
                // SourceScreenItemIndex: guide.position,  // P2
                ProgramId: guide.programId,
                ProgramSessionNum: guide.position,
                ProgramName: program?.title,
                ContentType: program?.contentType?.toLowerCase(),
                PackClass: packClass,
                SourceScreenName: screenName,
                TrackURL: guide.assets?.[0]?.url,
                PercentCompleted: percentCompleted,
                FilterName: trackFilterName,
                ...trackProps,
                // LoggedHourLocal is attached at emit time by
                // mapBaseEventProps for every tracked event.
            }).catch((err) =>
                logger.error('Failed to track event', {}, err as Error)
            );
        },
        [
            query.category,
            programFromContext,
            track,
            isShowingSubtitles,
            screenName,
        ]
    );

    const trackPackItemEvent = useCallback(
        ({
            eventName,
            guide,
            packTitle,
            program: providedProgram,
            trackProps,
            clearAmbient = false,
        }: TrackPackItemEventProps) => {
            const program = providedProgram ?? programFromContext;

            if (!guide && !packTitle) {
                logger.error(
                    'No guide or packTitle provided',
                    { eventName, guide },
                    {
                        name: 'No guide or packTitle provided',
                        message:
                            'useTrackSessionEvent: trackPackItemEvent : No guide or pack provided',
                    }
                );
                return;
            }

            // Surface-level context (home PackClass / RowIndex / RowItemIndex)
            // is auto-attached by `track` from the enclosing
            // `AnalyticsEventPropsProvider`. Pass `clearAmbient: true` to
            // opt out (e.g. card CTA buttons whose events shouldn't carry
            // home-row attribution).
            track(
                eventName,
                {
                    ...trackProps,
                    PackName: packTitle,
                    PackItemName: guide?.title,
                    ProgramId: guide?.programId,
                    ProgramName: program?.title,
                    ProgramSessionNum: guide?.isTrailer ? 0 : guide?.position,
                    ContentType: program?.contentType?.toLowerCase(),
                    IsClinicalProgram: program?.isClinical,
                    GuideId: guide ? getGuideId(guide) : undefined,
                    GuideName: guide?.title,
                    MediaType: guide?.assets?.[0]?.type,
                    ScreenName: screenName,
                    ...(guide?.position
                        ? { Position: guide.position + 1 }
                        : {}),
                },
                { clearAmbient }
            ).catch((err) =>
                logger.error('Failed to track event', {}, err as Error)
            );
        },
        [programFromContext, track, screenName]
    );

    const trackClinicalProgramEvent = useCallback(
        ({
            eventName,
            guide,
            program: providedProgram,
            trackProps,
        }: TrackPackItemEventProps) => {
            const program = programFromContext ?? providedProgram;

            if (!guide) {
                logger.error(
                    'No guide or packTitle provided',
                    { eventName, guide },
                    {
                        name: 'No guide provided',
                        message:
                            'useTrackSessionEvent: trackClinicalProgramEvent : No guide provided',
                    }
                );
                return;
            }

            track(eventName, {
                ...trackProps,
                ProgramName: program?.title,
                ProgramId: guide?.programId,
                ProgramSessionNum: guide?.isTrailer ? 0 : guide?.position,
                GuideId: guide ? getGuideId(guide) : undefined,
                GuideName: guide?.title,
                ScreenName: screenName,
            }).catch((err) =>
                logger.error('Failed to track event', {}, err as Error)
            );
        },
        [programFromContext, track, screenName]
    );

    return { trackSessionEvent, trackPackItemEvent, trackClinicalProgramEvent };
};
